import dayjs from 'dayjs';
import { authorizationStatusLabel } from '../../../../appointments/v2/utils/billingType';

/** Formatação e rótulos dos pedidos médicos e guias (Fase 2 de convênios). */

export const formatDate = (value) => (value ? dayjs(value).format('DD/MM/YYYY') : '');

/** Data preenchida e completa (a digitação parcial chega como dayjs inválido). */
export const isValidDate = (value) => !!value && dayjs(value).isValid();

/** Campo de data com algo digitado que não forma uma data. */
export const isIncompleteDate = (value) => !!value && !dayjs(value).isValid();

/** Data do formulário (dayjs ou null) no formato que a API espera. */
export const toApiDate = (value) => (value && dayjs(value).isValid() ? dayjs(value).format('YYYY-MM-DD') : '');

/** Texto da API (YYYY-MM-DD) para o DatePicker. */
export const fromApiDate = (value) => (value ? dayjs(value) : null);

export const plural = (count, one, many) => `${count} ${Number(count) === 1 ? one : many}`;

export const attachmentsLabel = (count) => (Number(count) > 0 ? plural(count, 'anexo', 'anexos') : 'Sem anexos');

/** Guia com quantidade autorizada e todas as sessões já usadas (continua vinculável). */
export const sessionsUsedUp = (authorization) => Number(authorization?.authorized_quantity) > 0
  && (Number(authorization?.used_count) || 0) >= Number(authorization.authorized_quantity);

/** "usado em 3 atendimentos" / "usada em 1 atendimento" / "ainda não usado". */
export const usageLabel = (count, feminine = false) => {
  const word = feminine ? 'usada' : 'usado';
  return Number(count) > 0 ? `${word} em ${plural(count, 'atendimento', 'atendimentos')}` : `ainda não ${word}`;
};

/**
 * Vencimento da guia, com o mesmo termo do pedido médico (requestValidityInfo):
 * "Vence em", "Venceu em" ou "Sem vencimento" (sem data, nunca vencida).
 * `expired` vem calculado pelo backend.
 */
export function validityInfo(validUntil, expired) {
  if (!validUntil) return { label: 'Sem vencimento', tone: 'muted', expired: false };
  const date = formatDate(validUntil);
  // O chip 'Vencido(a)' ao lado do título já diz a situação; aqui só a data
  if (expired) return { label: `Venceu em ${date}`, tone: 'expired', expired: true };
  return { label: `Vence em ${date}`, tone: 'ok', expired: false };
}

/**
 * Prazo do aviso "vence em breve", em dias a partir de hoje. Mesmo valor de
 * InsurancePendencies::SOON_DAYS (backend), que gera os avisos da agenda e do Início.
 */
export const EXPIRING_SOON_DAYS = 30;

/**
 * Vencimento do pedido médico, sempre com o termo "vencimento": "Vence em",
 * "Venceu em" ou "Sem vencimento". `badge` é o selo ao lado do título:
 * "Vencido", "Vence hoje" ou "Vence em breve" (até EXPIRING_SOON_DAYS dias).
 * `expired` vem calculado pelo backend.
 */
export function requestValidityInfo(validUntil, expired) {
  if (!validUntil) return { label: 'Sem vencimento', tone: 'muted', expired: false, badge: null };
  const date = formatDate(validUntil);
  if (expired) return { label: `Venceu em ${date}`, tone: 'expired', expired: true, badge: 'Vencido' };
  const days = dayjs(validUntil).startOf('day').diff(dayjs().startOf('day'), 'day');
  let badge = null;
  if (days <= 0) badge = 'Vence hoje';
  else if (days <= EXPIRING_SOON_DAYS) badge = 'Vence em breve';
  return { label: `Vence em ${date}`, tone: 'ok', expired: false, badge };
}

/** Rótulo curto da situação para o chip da linha; o menu usa o rótulo completo. */
export function authorizationStatusShortLabel(status) {
  switch (status) {
    case 'authorized': return 'Autorizada';
    case 'denied': return 'Negada';
    case 'cancelled': return 'Cancelada';
    case 'pending': return 'Aguardando';
    default: return authorizationStatusLabel(status);
  }
}

/** Número da guia ou o texto de ausência. */
export const referenceLabel = (authorization) => (
  authorization?.reference_number ? `Guia ${authorization.reference_number}` : 'Sem número'
);

/** Carteirinhas ativas da operadora escolhida. */
export const cardsForProvider = (insurances, providerId) => (insurances || []).filter(
  (card) => card.active !== 0 && card.active !== false && String(card.provider_id) === String(providerId),
);

/** Operadora pré-selecionada: a da única carteirinha ativa (ou a comum a todas). */
export function defaultProviderId(insurances, providers) {
  const active = (insurances || []).filter((card) => card.active !== 0 && card.active !== false);
  const ids = [...new Set(active.map((card) => String(card.provider_id)))];
  if (ids.length === 1) return ids[0];
  if ((providers || []).length === 1) return String(providers[0].Id);
  return '';
}

/**
 * Prazos prontos do vencimento do pedido médico, contados da emissão.
 * 'custom' = a recepção escolhe a data; 'none' = sem vencimento.
 */
export const REQUEST_VALIDITY_OPTIONS = [
  { value: '30d', label: '30 dias', amount: 30, unit: 'day' },
  { value: '3m', label: '3 meses', amount: 3, unit: 'month' },
  { value: '6m', label: '6 meses', amount: 6, unit: 'month' },
  { value: '1y', label: '1 ano', amount: 1, unit: 'year' },
  { value: 'custom', label: 'Outra data' },
  { value: 'none', label: 'Sem vencimento' },
];

export const DEFAULT_REQUEST_VALIDITY = '6m';

/** Vencimento de um prazo pronto a partir da emissão (null se não der para calcular). */
export function validityFromPreset(issuedAt, preset) {
  const option = REQUEST_VALIDITY_OPTIONS.find((item) => item.value === preset);
  if (!option?.amount || !isValidDate(issuedAt)) return null;
  return dayjs(issuedAt).add(option.amount, option.unit);
}

/**
 * Prazo com que um pedido já gravado abre na edição: 'custom' com a data salva
 * (mudar a emissão não mexe no vencimento) ou 'none' quando não tem vencimento.
 * Os prazos prontos valem no pedido novo e quando a pessoa escolhe um de propósito.
 */
export function presetFromValidity(validUntil) {
  return validUntil ? 'custom' : 'none';
}
